// Okno školení přes celou obrazovku (16. 10. 2026, schválený mockup): vlevo moduly, vpravo
// video a přílohy modulu, dole Předchozí / Další. Trenér: modul se označí jako hotový, když
// video (YouTube) doběhne do konce nebo tlačítkem „Dokoukáno“ (Loom), příloha po otevření.
// Správa (HR): stejné okno, navíc úpravy, přidělení a mazání položek.
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronDown, KeyRound, ChevronLeft, ChevronRight, ExternalLink, FileText, ListVideo, Monitor, Pencil, Play, Plus, Smartphone, Trash2, UserPlus } from "lucide-react";
import { Modal, HlavickaOkna } from "./ui.jsx";
import { Pil } from "./sales/salesUi.jsx";
import { rozpoznejOdkaz, kratkyNazev } from "../lib/skoleniVideo.js";

const krestni = (j) => String(j || "").split(" ")[0];

function Kolecko({ n, hotovo, aktivni }) {
  return (
    <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[11px] font-bold ${hotovo ? "bg-emerald-500 text-white" : aktivni ? "bg-brand-700 text-white" : "bg-slate-100 text-slate-500"}`}>
      {hotovo ? <Check className="h-3.5 w-3.5" /> : n}
    </span>
  );
}

function Zaskrtnuti({ zap }) {
  return (
    <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-md border ${zap ? "border-emerald-500 bg-emerald-500 text-white" : "border-slate-300 bg-white"}`}>
      {zap && <Check className="h-3.5 w-3.5" />}
    </span>
  );
}

function Prehravac({ m, trener, onKonec }) {
  const [varianta, setVarianta] = useState("mobil"); // výchozí je mobil, PC jen když má modul druhé video
  useEffect(() => { setVarianta("mobil"); }, [m.id]);
  const maPC = !!(m.url && m.url_pc);
  const adresa = maPC && varianta === "pc" ? m.url_pc : m.url;
  const o = useMemo(() => rozpoznejOdkaz(adresa), [adresa]);
  const ref = useRef(null);
  const hotovoRef = useRef(!!m.hotovo_at);
  hotovoRef.current = !!m.hotovo_at;
  const odeslano = useRef(null); // YouTube hlásí konec dvakrát, hotovo se pošle jednou za modul

  useEffect(() => {
    if (o.druh !== "youtube" || !trener) return undefined;
    const naZpravu = (e) => {
      if (!ref.current || e.source !== ref.current.contentWindow) return;
      let d = e.data;
      if (typeof d === "string") { try { d = JSON.parse(d); } catch { return; } }
      if (!d) return;
      const konec = (d.event === "onStateChange" && d.info === 0) || (d.event === "infoDelivery" && d.info && d.info.playerState === 0);
      if (konec && !hotovoRef.current && odeslano.current !== m.id) { odeslano.current = m.id; onKonec(); }
    };
    window.addEventListener("message", naZpravu);
    return () => window.removeEventListener("message", naZpravu);
  }, [o.druh, o.id, m.id, trener, onKonec]);

  function nacteno() {
    const w = ref.current?.contentWindow;
    if (!w) return;
    const posli = () => w.postMessage(JSON.stringify({ event: "listening", id: 1, channel: "widget" }), "*");
    posli(); setTimeout(posli, 600); setTimeout(posli, 1800);
  }

  if (o.druh === "youtube" || o.druh === "loom") {
    return (
      <div className="mx-auto" style={{ width: "min(100%, calc((100dvh - 290px) * 16 / 9))" }}>
        {maPC && (
          <div className="mb-2 flex flex-col gap-1.5 sm:flex-row sm:items-center sm:gap-2">
            <span className="text-[13px] font-semibold text-slate-500">Ukázka na</span>
            <div role="tablist" aria-label="Ukázka na" className="flex w-full rounded-xl bg-brand-50 p-1 sm:inline-flex sm:w-auto">
              {[["mobil", "Mobil", Smartphone], ["pc", "PC", Monitor]].map(([k, t, Ikona]) => (
                <button key={k} type="button" role="tab" aria-selected={varianta === k} onClick={() => setVarianta(k)}
                  className={`inline-flex flex-1 items-center justify-center gap-2 rounded-lg px-4 py-3 text-[15px] font-bold transition sm:flex-none sm:gap-1.5 sm:px-3 sm:py-1.5 sm:text-[13px] ${varianta === k ? "bg-brand-700 text-white shadow-sm" : "text-brand-800 hover:bg-white"}`}>
                  <Ikona className="h-5 w-5 sm:h-4 sm:w-4" />{t}
                </button>
              ))}
            </div>
          </div>
        )}
        <div className="overflow-hidden rounded-2xl bg-black" style={{ aspectRatio: "16 / 9" }}>
        <iframe key={`${m.id}-${varianta}`} ref={ref} title={m.nazev} src={o.embed} className="h-full w-full border-0" onLoad={nacteno}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen" allowFullScreen />
        </div>
      </div>
    );
  }
  return (
    <div className="flex flex-col items-center justify-center gap-3 rounded-2xl bg-slate-50 px-4 py-10 text-center" style={{ minHeight: 200 }}>
      <FileText className="h-8 w-8 text-brand-700" />
      <div className="text-sm font-semibold text-slate-700">Tenhle obsah se otevírá v novém panelu.</div>
      {m.url && <a className="btn btn-primary" href={m.url} target="_blank" rel="noopener noreferrer"><ExternalLink className="h-4 w-4" />Otevřít</a>}
    </div>
  );
}

export default function SkoleniOkno({ nazev, moduly, startId, rezim = "trener", odznaky = null, ztlumit = false, onClose, onOtevreno, onHotovo, sprava = null }) {
  const trener = rezim === "trener";
  const [idx, setIdx] = useState(() => Math.max(0, moduly.findIndex((m) => m.id === startId)));
  const [seznam, setSeznam] = useState(false);
  const [ukladam, setUkladam] = useState(false);
  async function dokoukano(x) { if (ukladam) return; setUkladam(true); try { await onHotovo?.(x); } finally { setUkladam(false); } }
  useEffect(() => { if (idx > moduly.length - 1) setIdx(Math.max(0, moduly.length - 1)); }, [moduly.length, idx]);
  const m = moduly[idx];
  const hotovych = moduly.filter((x) => x.hotovo_at).length;
  const procent = moduly.length ? Math.round((hotovych / moduly.length) * 100) : 0;

  useEffect(() => { if (trener && m && !m.otevreno_at) onOtevreno?.(m); /* jen při přepnutí modulu */ }, [m?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const o = m ? rozpoznejOdkaz(m.url) : null;
  const lide = m?.lide || [];
  const prejdi = (i) => { setIdx(Math.min(Math.max(i, 0), moduly.length - 1)); setSeznam(false); };

  const hlavicka = (
    <div className="overflow-hidden rounded-t-2xl bg-gradient-to-r from-brand-950 via-brand-900 to-brand-700">
      <HlavickaOkna ikona={Play} nazev={nazev} onClose={onClose}
        odznaky={<>
          <Pil ton="znacka" casti={`Modul ${Math.min(idx + 1, moduly.length)} z ${moduly.length}`} mala />
          {trener && <Pil ton={hotovych === moduly.length && moduly.length ? "ok" : "neutralni"} casti={`Hotovo ${hotovych} z ${moduly.length}`} mala />}
          {odznaky}
        </>} />
      {trener && (
        <div className="px-4 pb-3 sm:px-5">
          <div className="h-1.5 overflow-hidden rounded-full bg-white/20" role="progressbar" aria-valuenow={procent} aria-valuemin={0} aria-valuemax={100}>
            <div className="h-full rounded-full bg-emerald-400 transition-all" style={{ width: `${procent}%` }} />
          </div>
        </div>
      )}
    </div>
  );

  const paticka = (
    <div className="flex items-center justify-between gap-3 pb-3">
      <button type="button" className="btn btn-secondary" disabled={idx <= 0} onClick={() => prejdi(idx - 1)}><ChevronLeft className="h-4 w-4" /><span className="hidden sm:inline">Předchozí</span></button>
      <span className="text-[13px] font-semibold text-slate-500">Modul {Math.min(idx + 1, moduly.length)} z {moduly.length}</span>
      <button type="button" className="btn btn-primary" disabled={idx >= moduly.length - 1} onClick={() => prejdi(idx + 1)}><span className="hidden sm:inline">Další</span><ChevronRight className="h-4 w-4" /></button>
    </div>
  );

  const polozkySeznamu = (
    <div className="space-y-0.5">
      {moduly.map((x, i) => {
        const akt = i === idx;
        const hot = x.hotovo_at;
        const lx = x.lide || [];
        return (
          <div key={x.id}>
            <button type="button" onClick={() => prejdi(i)} aria-current={akt ? "true" : undefined}
              className={`flex w-full items-center gap-2.5 rounded-xl px-2.5 py-2 text-left ${akt ? "bg-brand-50" : "hover:bg-slate-50"}`} style={{ minHeight: 44 }}>
              <Kolecko n={i + 1} hotovo={trener && hot} aktivni={akt} />
              <span className={`min-w-0 flex-1 truncate text-[13px] ${akt ? "font-bold text-brand-deep" : "font-semibold text-slate-700"}`}>{kratkyNazev(x.nazev)}</span>
              {!trener && lx.length > 0 && <span className="text-[11px] font-semibold text-slate-400">{lx.filter((l) => l.hotovo_at).length}/{lx.length}</span>}
              {x.delka && <span className="text-[11px] text-slate-400">{String(x.delka).replace(" min", "")}</span>}
            </button>
            {akt && x.prilohy.map((p) => (
              <div key={p.id} className="flex items-center gap-2 py-1 pl-11 pr-2 text-[12px] text-slate-600">
                {trener ? <Zaskrtnuti zap={!!p.otevreno_at} /> : <FileText className="h-3.5 w-3.5 text-slate-400" />}
                <span className="min-w-0 flex-1 truncate">{kratkyNazev(p.nazev)}</span>
              </div>
            ))}
          </div>
        );
      })}
      {sprava && (
        <div className="space-y-2 pt-3">
          <button type="button" className="btn btn-secondary w-full" onClick={sprava.onPridatModul}><Plus className="h-4 w-4" />Přidat modul</button>
          <button type="button" className="btn btn-secondary w-full" onClick={sprava.onPridelitSkoleni}><UserPlus className="h-4 w-4" />Přidělit celé školení</button>
        </div>
      )}
    </div>
  );

  return (
    <Modal open onClose={ztlumit ? undefined : onClose} size="full" plnaHlavicka header={hlavicka} footer={moduly.length > 1 ? paticka : null}>
      {!m ? (
        <div className="py-10 text-center text-sm text-slate-500">Školení zatím nemá žádný modul.</div>
      ) : (
        <div className="grid gap-4 md:grid-cols-[280px_minmax(0,1fr)]">
          {/* Telefon: seznam modulů se rozbalí tlačítkem nad videem */}
          <div className="md:hidden">
            <button type="button" className="btn btn-secondary w-full justify-between" onClick={() => setSeznam((v) => !v)} aria-expanded={seznam}>
              <span className="inline-flex items-center gap-2"><ListVideo className="h-4 w-4" />Moduly ({idx + 1} z {moduly.length})</span>
              <ChevronDown className={`h-4 w-4 transition ${seznam ? "rotate-180" : ""}`} />
            </button>
            {seznam && <div className="mt-2 rounded-2xl border border-slate-200 p-2">{polozkySeznamu}</div>}
          </div>
          <aside className="hidden md:block">{polozkySeznamu}</aside>

          <div className="min-w-0 space-y-4">
            <Prehravac m={m} trener={trener} onKonec={() => onHotovo?.(m)} />
            <div>
              <h3 className="font-serif text-xl font-bold leading-tight text-slate-900">{m.nazev}</h3>
              <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                <Pil ton="info" casti="video" mala />
                {m.delka && <Pil ton="neutralni" casti={m.delka} mala />}
                {trener && (m.hotovo_at ? <Pil ton="ok" casti="hotovo" mala /> : <Pil ton="pozor" casti="čeká na dokoukání" mala />)}
                {!trener && lide.slice(0, 8).map((l) => <Pil key={l.person_id} ton={l.hotovo_at ? "ok" : "neutralni"} casti={krestni(l.person_name)} mala />)}
              </div>
              {m.pristup && (
                <div className="mt-3 flex items-start gap-3 rounded-2xl border border-amber-300 bg-amber-50 px-3.5 py-3">
                  <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-amber-200 text-amber-900"><KeyRound className="h-5 w-5" /></span>
                  <div className="min-w-0">
                    <div className="text-[11px] font-extrabold uppercase tracking-wide text-amber-800">Přístupové údaje</div>
                    <div className="whitespace-pre-line break-words text-base font-bold text-amber-950">{m.pristup}</div>
                  </div>
                </div>
              )}
              {m.popis && <p className="mt-2 text-[13px] text-slate-600">{m.popis}</p>}
              <div className="mt-3 flex flex-wrap gap-2">
                {trener && !m.hotovo_at && (
                  <button type="button" className={`btn ${o?.druh === "youtube" ? "btn-secondary" : "btn-primary"}`} disabled={ukladam} onClick={() => dokoukano(m)}>
                    <Check className="h-4 w-4" />{o?.druh === "youtube" ? "Označit jako dokoukáno" : "Dokoukáno"}
                  </button>
                )}
                {trener && m.test_token && !m.test_hotovo_at && (
                  <a className="btn btn-primary" href={`/skoleni/${m.test_token}`} target="_blank" rel="noopener noreferrer">Spustit test{m.test_nazev ? `: ${m.test_nazev}` : ""}</a>
                )}
                {trener && m.test_hotovo_at && <Pil ton="ok" casti={`test hotový${m.test_procent != null ? `, ${m.test_procent} %` : ""}`} />}
                {sprava && (
                  <>
                    <button type="button" className="btn btn-secondary" onClick={() => sprava.onUpravit(m)}><Pencil className="h-4 w-4" />Upravit</button>
                    <button type="button" className="btn btn-secondary" onClick={() => sprava.onPridelit(m)}><UserPlus className="h-4 w-4" />Přidělit modul</button>
                    <button type="button" className="btn btn-secondary" onClick={() => sprava.onPridatPrilohu(m)}><Plus className="h-4 w-4" />Přidat přílohu</button>
                    <button type="button" className="btn btn-secondary" onClick={() => sprava.onSmazat(m)}><Trash2 className="h-4 w-4" />Smazat</button>
                  </>
                )}
              </div>
            </div>

            {m.prilohy.length > 0 && (
              <div>
                <div className="mb-2 text-[12px] font-bold uppercase tracking-wide text-slate-400">
                  Přílohy modulu ({trener ? `${m.prilohy.filter((p) => p.otevreno_at).length} z ${m.prilohy.length} otevřeno` : m.prilohy.length})
                </div>
                <div className="grid gap-2 md:grid-cols-2">
                  {m.prilohy.map((p) => {
                    const akt = () => { if (p.url) window.open(p.url, "_blank", "noopener"); if (trener && !p.otevreno_at) onOtevreno?.(p); };
                    return (
                      <div key={p.id} className="flex items-center gap-3 rounded-xl border border-slate-200 px-3 py-2.5">
                        {trener && <Zaskrtnuti zap={!!p.otevreno_at} />}
                        <button type="button" className="min-w-0 flex-1 text-left" onClick={akt}>
                          <span className="block truncate text-[13px] font-semibold text-slate-800">{kratkyNazev(p.nazev)}</span>
                          <span className="block text-[11px] text-slate-400">{p.typ === "dokument" ? "odkaz, dokument" : "video"}{trener ? (p.otevreno_at ? ", otevřeno" : ", neotevřeno") : ""}</span>
                        </button>
                        {sprava ? (
                          <span className="flex gap-1">
                            <button type="button" className="btn-ikona" onClick={() => sprava.onUpravit(p)} aria-label="Upravit přílohu" title="Upravit"><Pencil className="h-4 w-4" /></button>
                            <button type="button" className="btn-ikona" onClick={() => sprava.onSmazat(p)} aria-label="Smazat přílohu" title="Smazat"><Trash2 className="h-4 w-4" /></button>
                          </span>
                        ) : (
                          <button type="button" className="btn-ikona" onClick={akt} aria-label={`Otevřít ${kratkyNazev(p.nazev)}`} title="Otevřít"><ExternalLink className="h-4 w-4" /></button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
}
